import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Image as ImageIcon, X, MagnifyingGlass } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useDataCache } from '@/contexts/DataCache';
import { compressImage } from '@/lib/imageCompress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  sku: '', name: '', description: '', brand: '', category_id: 'none',
  image_url: '', extra_images: [],
  precio_1: '', precio_2: '', precio_3: '', precio_4: '', precio_5: '',
  mayoreo: '', medio_mayoreo: '', default_commission: '0',
  components: [],
  is_new_arrival: false,
  product_info: '',
  has_promo: false,
  promo_type: 'flat',
  promo_price: '',
  promo_min_qty: '',
  promo_end_date: '',
  promo_description: '',
  promo_local_only: false,
  promo_shipment_only: false,
  visible_in_catalog: true,
};

const toFormFromPkg = (pkg) => ({
  sku: pkg?.sku || '',
  name: pkg?.name || '',
  description: pkg?.description || '',
  brand: pkg?.brand || '',
  category_id: pkg?.category_id || 'none',
  image_url: pkg?.image_url || '',
  extra_images: pkg?.extra_images || [],
  precio_1: pkg?.precio_1 ?? '',
  precio_2: pkg?.precio_2 ?? '',
  precio_3: pkg?.precio_3 ?? '',
  precio_4: pkg?.precio_4 ?? '',
  precio_5: pkg?.precio_5 ?? '',
  mayoreo: pkg?.mayoreo ?? '',
  medio_mayoreo: pkg?.medio_mayoreo ?? '',
  default_commission: pkg?.default_commission ?? 0,
  components: (pkg?.components || []).map(c => ({ ...c })),
  is_new_arrival: !!pkg?.is_new_arrival,
  product_info: pkg?.product_info || '',
  has_promo: !!pkg?.has_promo,
  promo_type: pkg?.promo_type || 'flat',
  promo_price: pkg?.promo_price ?? '',
  promo_min_qty: pkg?.promo_min_qty ?? '',
  promo_end_date: pkg?.promo_end_date || '',
  promo_description: pkg?.promo_description || '',
  promo_local_only: !!pkg?.promo_local_only,
  promo_shipment_only: !!pkg?.promo_shipment_only,
  visible_in_catalog: pkg?.visible_in_catalog !== false,
});

/**
 * Diálogo reutilizable para editar/crear paquetes.
 *   - `pkg`: objeto completo (si está presente, es edición). Si no, se crea nuevo.
 *   - `onSaved(pkg)`: callback después de guardar correctamente.
 */
export const PackageEditDialog = ({ open, onClose, pkg, onSaved }) => {
  const { products, categories, refreshPackages } = useDataCache();
  const [form, setForm] = useState(emptyForm);
  const [compSearch, setCompSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [promoDialog, setPromoDialog] = useState(false);
  const editingId = pkg?.id || null;

  useEffect(() => {
    if (!open) return;
    setForm(pkg ? toFormFromPkg(pkg) : emptyForm);
    setCompSearch('');
  }, [open, pkg]);

  const productsForSelect = useMemo(() => {
    const parentIds = new Set(products.map(p => p.parent_product_id).filter(Boolean));
    const hiddenParentIds = new Set(
      products
        .filter(p => (p.has_variants || parentIds.has(p.id)) && p.visible_in_pos === false)
        .map(p => p.id)
    );
    return products.filter(p =>
      !p.has_variants &&
      !parentIds.has(p.id) &&
      p.visible_in_pos !== false &&
      !(p.parent_product_id && hiddenParentIds.has(p.parent_product_id))
    );
  }, [products]);

  const filteredProducts = useMemo(() => {
    const q = compSearch.trim().toLowerCase();
    if (!q) return productsForSelect.slice(0, 50);
    const tokens = q.split(/\s+/).filter(Boolean);
    return productsForSelect.filter(p => {
      const text = `${p.name || ''} ${p.variant_name || ''} ${p.sku || ''}`.toLowerCase();
      return tokens.every(t => text.includes(t));
    }).slice(0, 50);
  }, [productsForSelect, compSearch]);

  const addComponent = (product) => {
    const exists = form.components.find(c => c.product_id === product.id);
    if (exists) {
      setForm(f => ({
        ...f,
        components: f.components.map(c =>
          c.product_id === product.id ? { ...c, quantity: c.quantity + 1 } : c
        ),
      }));
    } else {
      setForm(f => ({
        ...f,
        components: [...f.components, {
          product_id: product.id,
          quantity: 1,
          name: product.name,
          variant_name: product.variant_name || null,
          sku: product.sku,
        }],
      }));
    }
  };

  const updateComponentQty = (product_id, qty) => {
    const q = Math.max(1, parseInt(qty) || 1);
    setForm(f => ({
      ...f,
      components: f.components.map(c =>
        c.product_id === product_id ? { ...c, quantity: q } : c
      ),
    }));
  };

  const removeComponent = (product_id) => {
    setForm(f => ({ ...f, components: f.components.filter(c => c.product_id !== product_id) }));
  };

  const handleImageUpload = async (e, isExtra = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await compressImage(file);
      if (isExtra) {
        setForm(f => ({ ...f, extra_images: [...(f.extra_images || []), dataUrl] }));
      } else {
        setForm(f => ({ ...f, image_url: dataUrl }));
      }
    } catch (err) {
      toast.error(err.message || 'No se pudo procesar la imagen');
    } finally {
      e.target.value = '';
    }
  };

  const removeExtraImage = (idx) => {
    setForm(f => ({ ...f, extra_images: f.extra_images.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.sku.trim()) {
      toast.error('SKU y nombre son obligatorios');
      return;
    }
    if (form.components.length === 0) {
      toast.error('Agrega al menos un componente al paquete');
      return;
    }
    setSaving(true);
    const toNum = (v) => parseFloat(v || 0) || 0;
    const payload = {
      sku: form.sku.trim(),
      name: form.name.trim(),
      description: form.description || null,
      brand: form.brand || null,
      category_id: form.category_id === 'none' ? null : form.category_id,
      image_url: form.image_url || null,
      extra_images: form.extra_images || [],
      precio_1: toNum(form.precio_1),
      precio_2: toNum(form.precio_2),
      precio_3: toNum(form.precio_3),
      precio_4: toNum(form.precio_4),
      precio_5: toNum(form.precio_5),
      mayoreo: toNum(form.mayoreo),
      medio_mayoreo: toNum(form.medio_mayoreo),
      default_commission: toNum(form.default_commission),
      components: form.components.map(c => ({
        product_id: c.product_id,
        quantity: parseInt(c.quantity) || 1,
        name: c.name,
        variant_name: c.variant_name,
        sku: c.sku,
      })),
      has_promo: !!form.has_promo,
      promo_type: form.has_promo ? form.promo_type : null,
      promo_price: form.has_promo && form.promo_price !== '' ? toNum(form.promo_price) : null,
      promo_min_qty: form.has_promo && form.promo_type === 'min_qty' && form.promo_min_qty !== ''
        ? parseInt(form.promo_min_qty) : null,
      promo_end_date: form.has_promo && form.promo_end_date ? form.promo_end_date : null,
      promo_description: form.has_promo ? (form.promo_description || null) : null,
      promo_local_only: form.has_promo ? !!form.promo_local_only : false,
      promo_shipment_only: form.has_promo ? !!form.promo_shipment_only : false,
      is_new_arrival: !!form.is_new_arrival,
      product_info: (form.product_info || '').trim() || null,
      visible_in_catalog: form.visible_in_catalog !== false,
    };
    try {
      let res;
      if (editingId) {
        res = await axios.put(`${API}/packages/${editingId}`, payload, { withCredentials: true });
        toast.success('Paquete actualizado');
      } else {
        res = await axios.post(`${API}/packages`, payload, { withCredentials: true });
        toast.success('Paquete creado');
      }
      refreshPackages?.();
      onSaved?.(res?.data || payload);
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar Paquete' : 'Nuevo Paquete'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="package-form">
            <Tabs defaultValue="general" className="w-full">
              <TabsList className="grid grid-cols-3">
                <TabsTrigger value="general">General</TabsTrigger>
                <TabsTrigger value="precios">Precios</TabsTrigger>
                <TabsTrigger value="componentes">Componentes ({form.components.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="general" className="space-y-4 pt-4">
                <div>
                  <Label>Imagen principal</Label>
                  <div className="flex items-center gap-3 mt-1">
                    {form.image_url ? (
                      <div className="relative">
                        <img src={form.image_url} alt="" className="w-20 h-20 object-cover rounded" />
                        <button type="button" onClick={() => setForm(f => ({ ...f, image_url: '' }))}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="w-20 h-20 bg-slate-100 rounded flex items-center justify-center">
                        <ImageIcon size={32} className="text-slate-400" />
                      </div>
                    )}
                    <Input type="file" accept="image/*,.heic,.heif" onChange={(e) => handleImageUpload(e, false)} data-testid="package-image-input" />
                  </div>
                </div>

                <div>
                  <Label>Imágenes adicionales (galería)</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {(form.extra_images || []).map((img, idx) => (
                      <div key={idx} className="relative">
                        <img src={img} alt="" className="w-16 h-16 object-cover rounded" />
                        <button type="button" onClick={() => removeExtraImage(idx)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                    <label className="w-16 h-16 border-2 border-dashed border-slate-300 rounded flex items-center justify-center cursor-pointer hover:border-violet-500 hover:bg-violet-50">
                      <input type="file" accept="image/*,.heic,.heif" multiple className="hidden"
                        onChange={async (e) => {
                          const files = Array.from(e.target.files || []);
                          for (const f of files) {
                            try {
                              const du = await compressImage(f);
                              setForm(prev => ({ ...prev, extra_images: [...(prev.extra_images || []), du] }));
                            } catch (err) {
                              toast.error(`${f.name}: ${err.message}`);
                            }
                          }
                          e.target.value = '';
                        }} />
                      <span className="text-xl text-slate-400">+</span>
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>SKU del paquete *</Label>
                    <Input value={form.sku} onChange={e => setForm(f => ({ ...f, sku: e.target.value }))} required data-testid="package-sku" />
                  </div>
                  <div>
                    <Label>Nombre *</Label>
                    <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required data-testid="package-name" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Marca</Label>
                    <Input value={form.brand} onChange={e => setForm(f => ({ ...f, brand: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Categoría</Label>
                    <Select value={form.category_id} onValueChange={v => setForm(f => ({ ...f, category_id: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin categoría</SelectItem>
                        {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Descripción</Label>
                  <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                </div>

                <div className="bg-slate-50 border rounded p-3 space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!form.is_new_arrival}
                      onChange={(e) => setForm(f => ({ ...f, is_new_arrival: e.target.checked }))}
                      className="w-4 h-4"
                      data-testid="package-is-new"
                    />
                    <span className="text-sm font-semibold">
                      🆕 Producto nuevo <span className="text-[10px] text-slate-500">(muestra badge &quot;NUEVO&quot; en el catálogo por 10 días)</span>
                    </span>
                  </label>
                  <div>
                    <Label className="text-xs">Información del producto (botón <b>i</b> del catálogo público)</Label>
                    <textarea
                      value={form.product_info}
                      onChange={(e) => setForm(f => ({ ...f, product_info: e.target.value }))}
                      placeholder="Ej: Incluye 5 sillas apilables Victoria de resina."
                      className="w-full mt-1 border rounded px-2 py-1.5 text-sm min-h-[70px]"
                      data-testid="package-product-info"
                    />
                  </div>
                  <label className="flex items-start gap-2 cursor-pointer select-none border-t border-slate-200 pt-3">
                    <input
                      type="checkbox"
                      checked={form.visible_in_catalog !== false}
                      onChange={(e) => setForm(f => ({ ...f, visible_in_catalog: e.target.checked }))}
                      className="w-4 h-4 mt-0.5"
                      data-testid="package-visible-in-catalog"
                    />
                    <span className="text-sm">
                      <b>Visible en Catálogo y Ofertas</b>
                      <span className="block text-[11px] text-slate-500 mt-0.5">
                        Si lo apagas, este paquete no aparecerá en la página pública (sigue disponible en POS).
                      </span>
                    </span>
                  </label>
                </div>
              </TabsContent>

              <TabsContent value="precios" className="space-y-4 pt-4">
                <div className="grid grid-cols-3 gap-3">
                  {[1, 2, 3, 4, 5].map(n => (
                    <div key={n}>
                      <Label>Precio {n}</Label>
                      <Input type="number" step="0.01" value={form[`precio_${n}`]}
                        onChange={e => setForm(f => ({ ...f, [`precio_${n}`]: e.target.value }))}
                        data-testid={`package-precio-${n}`} />
                    </div>
                  ))}
                  <div>
                    <Label>Mayoreo</Label>
                    <Input type="number" step="0.01" value={form.mayoreo}
                      onChange={e => setForm(f => ({ ...f, mayoreo: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Medio mayoreo</Label>
                    <Input type="number" step="0.01" value={form.medio_mayoreo}
                      onChange={e => setForm(f => ({ ...f, medio_mayoreo: e.target.value }))} />
                  </div>
                  <div>
                    <Label className="text-violet-700 font-semibold">Comisión del paquete *</Label>
                    <Input type="number" step="0.01" value={form.default_commission}
                      onChange={e => setForm(f => ({ ...f, default_commission: e.target.value }))}
                      data-testid="package-commission" />
                    <p className="text-xs text-slate-500 mt-1">Comisión TOTAL al vendedor cuando se vende el paquete.</p>
                  </div>
                </div>

                <div className="mt-4 border-t pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-emerald-700 font-semibold flex items-center gap-2">
                      🏷️ Promoción del paquete
                    </Label>
                    <input
                      type="checkbox"
                      checked={!!form.has_promo}
                      onChange={(e) => {
                        setForm(f => ({ ...f, has_promo: e.target.checked }));
                        if (e.target.checked) setPromoDialog(true);
                      }}
                      className="h-5 w-5 cursor-pointer accent-emerald-600"
                      data-testid="package-has-promo"
                    />
                  </div>
                  {form.has_promo && (
                    <div className="bg-emerald-50 border border-emerald-300 rounded p-3 text-sm space-y-1">
                      <p>
                        {form.promo_type === 'min_qty' ? (
                          <>🏷️ <strong>${form.promo_price || '?'}</strong> si lleva ≥ <strong>{form.promo_min_qty || '?'}</strong> pzas</>
                        ) : (
                          <>🏷️ Precio fijo: <strong>${form.promo_price || '?'}</strong></>
                        )}
                      </p>
                      {form.promo_end_date && <p className="text-xs">📅 Vence: {form.promo_end_date}</p>}
                      <p className="text-xs">
                        {form.promo_local_only ? '🏪 Solo local' : form.promo_shipment_only ? '🚚 Solo envío' : '🏪🚚 Ambos'}
                      </p>
                      <Button type="button" size="sm" variant="outline" onClick={() => setPromoDialog(true)} data-testid="edit-package-promo-button">
                        Editar promoción
                      </Button>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="componentes" className="space-y-4 pt-4">
                <div className="border border-border rounded p-3">
                  <Label className="mb-2 block">Buscar producto/variante para agregar</Label>
                  <div className="relative">
                    <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input value={compSearch} onChange={e => setCompSearch(e.target.value)}
                      placeholder="Nombre, SKU o variante..." className="pl-10"
                      data-testid="component-search" />
                  </div>
                  <div className="mt-2 max-h-56 overflow-y-auto border border-border rounded">
                    {filteredProducts.length === 0 && (
                      <div className="p-3 text-sm text-slate-400">Sin resultados</div>
                    )}
                    {filteredProducts.map(p => (
                      <button type="button" key={p.id}
                        onClick={() => addComponent(p)}
                        className="w-full text-left px-3 py-2 hover:bg-violet-50 border-b border-border last:border-0 flex items-center gap-2"
                        data-testid={`add-component-${p.id}`}>
                        {p.image_url ? (
                          <img src={p.image_url} alt="" className="w-8 h-8 object-cover rounded" />
                        ) : (
                          <div className="w-8 h-8 bg-slate-100 rounded" />
                        )}
                        <div className="flex-1">
                          <div className="text-sm font-medium">
                            {p.variant_name ? `${p.name} – ${p.variant_name}` : p.name}
                          </div>
                          <div className="text-xs text-slate-500 font-mono">{p.sku} · stock: {p.stock || 0}</div>
                        </div>
                        <Plus size={16} className="text-violet-600" />
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Componentes incluidos en el paquete</Label>
                  {form.components.length === 0 && (
                    <p className="text-sm text-slate-400 mt-2">Aún no agregas ningún componente.</p>
                  )}
                  <div className="space-y-2 mt-2">
                    {form.components.map(c => (
                      <div key={c.product_id} className="flex items-center gap-2 bg-slate-50 rounded p-2"
                        data-testid={`component-row-${c.product_id}`}>
                        <div className="flex-1">
                          <div className="text-sm font-medium">{c.variant_name ? `${c.name} – ${c.variant_name}` : c.name}</div>
                          <div className="text-xs text-slate-500 font-mono">{c.sku}</div>
                        </div>
                        <Input type="number" min="1" value={c.quantity}
                          onChange={e => updateComponentQty(c.product_id, e.target.value)}
                          className="w-20" />
                        <span className="text-xs text-slate-500">u.</span>
                        <Button type="button" size="sm" variant="destructive"
                          onClick={() => removeComponent(c.product_id)}>
                          <X size={14} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <div className="flex justify-end gap-2 pt-4 border-t border-border">
              <Button type="button" variant="outline" onClick={() => onClose?.()}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="save-package-btn">
                {saving ? 'Guardando...' : (editingId ? 'Guardar cambios' : 'Crear paquete')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={promoDialog} onOpenChange={setPromoDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">🏷️ Configurar Promoción del Paquete</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Tipo de promoción</Label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button"
                  onClick={() => setForm(f => ({ ...f, promo_type: 'flat', promo_min_qty: '' }))}
                  className={`p-3 border-2 rounded text-sm btn-transition ${form.promo_type === 'flat' ? 'border-emerald-500 bg-emerald-50' : 'border-border hover:bg-slate-50'}`}
                  data-testid="package-promo-type-flat">
                  <div className="font-semibold">Precio fijo</div>
                  <div className="text-xs text-muted-foreground">Aplica siempre</div>
                </button>
                <button type="button"
                  onClick={() => setForm(f => ({ ...f, promo_type: 'min_qty' }))}
                  className={`p-3 border-2 rounded text-sm btn-transition ${form.promo_type === 'min_qty' ? 'border-emerald-500 bg-emerald-50' : 'border-border hover:bg-slate-50'}`}
                  data-testid="package-promo-type-min-qty">
                  <div className="font-semibold">Por volumen</div>
                  <div className="text-xs text-muted-foreground">Aplica con cantidad mínima</div>
                </button>
              </div>
            </div>

            <div>
              <Label>Precio en promoción ($)</Label>
              <Input type="number" step="0.01" value={form.promo_price}
                onChange={(e) => setForm(f => ({ ...f, promo_price: e.target.value }))}
                data-testid="package-promo-price-input" placeholder="Ej: 130.00" />
            </div>

            {form.promo_type === 'min_qty' && (
              <div>
                <Label>Cantidad mínima en carrito</Label>
                <Input type="number" min="1" value={form.promo_min_qty}
                  onChange={(e) => setForm(f => ({ ...f, promo_min_qty: e.target.value }))}
                  data-testid="package-promo-min-qty-input" placeholder="Ej: 3" />
              </div>
            )}

            <div>
              <Label>Descripción (opcional)</Label>
              <Input value={form.promo_description}
                onChange={(e) => setForm(f => ({ ...f, promo_description: e.target.value }))}
                placeholder="Ej. Combo verano" data-testid="package-promo-description-input" />
            </div>

            <div>
              <Label>Fecha de finalización (opcional)</Label>
              <Input type="date" value={form.promo_end_date}
                onChange={(e) => setForm(f => ({ ...f, promo_end_date: e.target.value }))}
                data-testid="package-promo-end-date-input" />
            </div>

            <div>
              <Label className="mb-2 block">Alcance de la promoción</Label>
              <div className="grid grid-cols-3 gap-2">
                <button type="button"
                  onClick={() => setForm(f => ({ ...f, promo_local_only: false, promo_shipment_only: false }))}
                  className={`p-2 border-2 rounded text-xs btn-transition ${(!form.promo_local_only && !form.promo_shipment_only) ? 'border-emerald-500 bg-emerald-50 font-semibold' : 'border-border hover:bg-slate-50'}`}
                  data-testid="package-promo-scope-both">🏪🚚 Ambos</button>
                <button type="button"
                  onClick={() => setForm(f => ({ ...f, promo_local_only: true, promo_shipment_only: false }))}
                  className={`p-2 border-2 rounded text-xs btn-transition ${form.promo_local_only ? 'border-emerald-500 bg-emerald-50 font-semibold' : 'border-border hover:bg-slate-50'}`}
                  data-testid="package-promo-scope-local">🏪 Solo local</button>
                <button type="button"
                  onClick={() => setForm(f => ({ ...f, promo_local_only: false, promo_shipment_only: true }))}
                  className={`p-2 border-2 rounded text-xs btn-transition ${form.promo_shipment_only ? 'border-emerald-500 bg-emerald-50 font-semibold' : 'border-border hover:bg-slate-50'}`}
                  data-testid="package-promo-scope-shipment">🚚 Solo envío</button>
              </div>
            </div>

            <div className="flex justify-between gap-2 pt-2">
              <Button type="button" variant="destructive"
                onClick={() => {
                  setForm(f => ({ ...f, has_promo: false, promo_price: '', promo_min_qty: '', promo_end_date: '', promo_description: '', promo_local_only: false, promo_shipment_only: false }));
                  setPromoDialog(false);
                }}
                data-testid="package-remove-promo-button">Quitar promo</Button>
              <Button type="button" onClick={() => setPromoDialog(false)} data-testid="package-save-promo-button">
                Guardar configuración
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PackageEditDialog;
